"use client";
import { useCallback, useMemo, useState } from "react";
import { DEFAULT_MAX_TRAVELERS } from "../constants.js";
import { getTravelerError } from "../helpers/getTravelerError.js";
import { getTravelerLabel } from "../helpers/getTravelerLabel.js";
const DEFAULT_ROOMS = 1;
const DEFAULT_ADULTS = 1;
const DEFAULT_CHILDREN = 0;
const DEFAULT_CHILDREN_AGES = {};
const DEFAULT_LAP_INFANTS = 0;
function useTravelerSelection({ ageSelectEnabled, errorMessage: externalErrorMessage, initialValues, isInternationalFlight, isWhiteLabel, maxTravelers = DEFAULT_MAX_TRAVELERS, shouldCountLapInfant = false, shouldAllowInternationalLapInfants }) {
    const [rooms, setRooms] = useState(initialValues?.rooms ?? DEFAULT_ROOMS);
    const [adults, setAdults] = useState(initialValues?.adults ?? DEFAULT_ADULTS);
    const [children, setChildren] = useState(initialValues?.children ?? DEFAULT_CHILDREN);
    const [childrenAges, setChildrenAges] = useState(initialValues?.childrenAges ?? DEFAULT_CHILDREN_AGES);
    const [lapInfants, setLapInfants] = useState(initialValues?.lapInfants ?? DEFAULT_LAP_INFANTS);
    const [shouldValidate, setShouldValidate] = useState(false);
    const label = useMemo(()=>getTravelerLabel({
            adults,
            children,
            lapInfants,
            rooms
        }), [
        adults,
        children,
        lapInfants,
        rooms
    ]);
    const errorMessage = useMemo(()=>externalErrorMessage ?? getTravelerError({
            children,
            childrenAges,
            lapInfants,
            ageSelectEnabled,
            isInternationalFlight,
            isWhiteLabel,
            shouldAllowInternationalLapInfants,
            shouldValidate
        }) ?? '', [
        externalErrorMessage,
        children,
        childrenAges,
        lapInfants,
        ageSelectEnabled,
        isInternationalFlight,
        isWhiteLabel,
        shouldAllowInternationalLapInfants,
        shouldValidate
    ]);
    const remainingTravelerCount = useMemo(()=>{
        const passengerCount = adults + children + (shouldCountLapInfant ? lapInfants : 0);
        return Math.max(maxTravelers - passengerCount, 0);
    }, [
        adults,
        children,
        lapInfants,
        shouldCountLapInfant,
        maxTravelers
    ]);
    const onAdultsChange = useCallback((count)=>{
        setAdults(count);
        setLapInfants((currentLapInfants)=>Math.min(count, currentLapInfants));
    }, []);
    const onChildrenChange = useCallback((count)=>{
        setChildren(count);
        setChildrenAges((currentAges)=>Object.fromEntries(Object.entries(currentAges).filter(([key])=>Number(key) < count)));
    }, []);
    const onAgesChange = useCallback((index, value)=>{
        setChildrenAges((currentAges)=>({
                ...currentAges,
                [index]: value
            }));
    }, []);
    const triggerValidation = useCallback(()=>{
        setShouldValidate(true);
    }, []);
    return useMemo(()=>({
            label,
            errorMessage,
            rooms,
            adults,
            children,
            childrenAges,
            lapInfants,
            remainingTravelerCount,
            shouldValidate,
            setRooms,
            setAdults: onAdultsChange,
            setChildren: onChildrenChange,
            setChildrenAges: onAgesChange,
            setLapInfants,
            triggerValidation
        }), [
        label,
        errorMessage,
        rooms,
        adults,
        children,
        childrenAges,
        lapInfants,
        remainingTravelerCount,
        shouldValidate,
        onAdultsChange,
        onChildrenChange,
        onAgesChange,
        triggerValidation
    ]);
}
export { useTravelerSelection };
